import { Grid, Typography } from '@material-ui/core';
import React from 'react';
import { useCampfireLazyQuery } from '../../../../../../../../../global/network/useCampfireLazyQuery';
import { ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK } from '../../../../admin-console-volunteer-profile.gql';
import { DropItemTrigger } from './DropItemTrigger';

interface CrossTaskDynamicFieldPreviewProps {
  field: any; // We'll update this type when the backend types are generated
  isDragging: boolean;
}

export function CrossTaskDynamicFieldPreview({
  field,
  isDragging: _unusedIsDragging,
}: CrossTaskDynamicFieldPreviewProps) {
  // For cross-task dynamic fields, the options come from the crossTaskDynamicOptions
  // These should be populated by the backend based on the sourceTaskId and sourceFieldId
  const options = field.crossTaskDynamicOptions || [];

  // Lazy query to get the source task details to resolve option names
  const [getSourceTask, { data: sourceTaskData }] = useCampfireLazyQuery(
    ADMIN_CONSOLE_GET_SINGLE_VOLUNTEER_PROFILE_TASK
  );

  // Fetch source task data when the field has source configuration
  React.useEffect(() => {
    if (field.sourceTaskId && !sourceTaskData) {
      getSourceTask({
        variables: { taskId: field.sourceTaskId },
      });
    }
  }, [field.sourceTaskId, sourceTaskData, getSourceTask]);

  // Function to resolve option name from option ID
  const getOptionName = (optionId: string): string => {
    if (!sourceTaskData?.vm?.task) return optionId;

    const { task } = sourceTaskData.vm;
    const sourceField = task.taskItems
      ?.filter((item: any) => item.__typename === 'VOLUNTEER_TaskItemFieldType' && item.field)
      ?.map((item: any) => item.field)
      ?.find((fieldItem: any) => fieldItem.taskFieldId === field.sourceFieldId);

    if (!sourceField) return optionId;

    // Find the option by ID based on field type
    let optionName = optionId;
    switch (sourceField.__typename) {
      case 'VOLUNTEER_DropdownTaskFieldType': {
        const dropdownOption = sourceField.dropdownOptions?.find((opt: any) => opt.dropdownFieldOptionId === optionId);
        if (dropdownOption) optionName = dropdownOption.name;
        break;
      }
      case 'VOLUNTEER_MultipleChoiceTaskFieldType': {
        const mcOption = sourceField.multipleChoiceOptions?.find(
          (opt: any) => opt.multipleChoiceFieldOptionId === optionId
        );
        if (mcOption) optionName = mcOption.name;
        break;
      }
      case 'VOLUNTEER_CheckboxTaskFieldType': {
        const cbOption = sourceField.checkboxOptions?.find((opt: any) => opt.checkboxFieldOptionId === optionId);
        if (cbOption) optionName = cbOption.name;
        break;
      }
      case 'VOLUNTEER_QuizTaskFieldType': {
        const quizOption = sourceField.quizTaskFieldOptions?.find((opt: any) => opt.quizTaskFieldOptionId === optionId);
        if (quizOption) optionName = quizOption.name;
        break;
      }
      default:
        break;
    }

    return optionName;
  };

  return (
    <Grid container direction='column' spacing={1}>
      <Grid item>
        <Typography variant='body2' color='primary' style={{ fontWeight: 'bold' }}>
          Cross-Task Dynamic Field
        </Typography>
        <Typography variant='body2' color='textSecondary'>
          References: {field.sourceTaskId ? 'Task configured' : 'No task selected'} |{' '}
          {field.sourceFieldId ? 'Field configured' : 'No field selected'}
        </Typography>
      </Grid>

      {options.length > 0 ? (
        <Grid item>
          <Grid container direction='column'>
            {options.map((option: any) => {
              // Use the sourceOptionValue as the optionId for the trigger system
              const optionId = option.sourceOptionValue;
              // Get the readable name from the source field options
              const optionName = getOptionName(optionId);

              return (
                <Grid item key={optionId} style={{ overflow: 'hidden', maxWidth: '100%' }}>
                  <DropItemTrigger optionId={optionId} name={optionName} />
                </Grid>
              );
            })}
          </Grid>
        </Grid>
      ) : (
        <Grid item>
          <Typography variant='body2' color='textSecondary' style={{ fontStyle: 'italic' }}>
            Trigger options will appear here once the source task and field are properly configured and the field is
            saved.
          </Typography>
        </Grid>
      )}
    </Grid>
  );
}
